import React, { useRef, useState } from "react";
import { DeleteOutlined, EditOutlined, ExportOutlined, FolderOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import { Bookmark, FolderTreeNodeProps, Item } from "./bookmark.types";
import { DropTargetMonitor, useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "./utils";


const FolderTreeNode: React.FC<FolderTreeNodeProps> = ({
	item,
	onSelect,
	onDrop,
	expandedKeys,
	setExpandedKeys,
	level,
	isSelected,
}) => {
	const nodeRef = useRef<HTMLElement | null>(null);
	const [dropPosition, setDropPosition] = useState<"above" | "below" | "inside" | null>(null);

	const [{ isDragging }, drag] = useDrag({
		type: ItemTypes.FOLDER,
		item: { id: item.id, type: item.type },
		collect: monitor => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [{ isOver }, drop] = useDrop({
		accept: [ItemTypes.FOLDER, ItemTypes.BOOKMARK],
		drop: (draggedItem: Item, monitor: DropTargetMonitor) => {
			if (!nodeRef.current || !dropPosition) return;
			onDrop(draggedItem, item.id, dropPosition);
		},
		hover: (draggedItem: Item, monitor: DropTargetMonitor) => {
			if (!nodeRef.current || draggedItem.id === item.id) {
				setDropPosition(null);
				return;
			}
			const offset = monitor.getClientOffset();
			if (!offset) {
				setDropPosition(null);
				return;
			}
			const rect = nodeRef.current.getBoundingClientRect();
			const y = offset.y - rect.top;
			const heightThird = rect.height / 2;
			if (y < heightThird / 2) {
				setDropPosition("above");
			} else if (y > rect.height - heightThird / 2) {
				setDropPosition("below");
			} else {
				setDropPosition("inside");
			}
		},
		canDrop: (draggedItem: Item) => {
			if (draggedItem.id === item.id) return false;
			if (draggedItem.type === "folder" && item.type === "folder") {
				const isDescendant = (node: Bookmark, id: string): boolean => {
					if (!Array.isArray(node.children)) return false;
					return node.children.some(child => child.id === id || isDescendant(child, id));
				};
				return !isDescendant(item, draggedItem.id);
			}
			return true;
		},
		collect: monitor => ({
			isOver: monitor.isOver(),
		}),
	});

	const isExpanded = expandedKeys.includes(item.id);
	const toggleExpand = (e: React.MouseEvent) => {
		e.stopPropagation();
		setExpandedKeys(
			isExpanded ? expandedKeys.filter(key => key !== item.id) : [...expandedKeys, item.id]
		);
	};

	return (
		<div
			ref={node => {
				nodeRef.current = node;
				drag(drop(node));
			}}
			className={`folder-node ${isSelected ? "selected" : ""} ${
				isOver && dropPosition === "inside" ? "dnd-over" : ""
			}`}
			style={{
				padding: `4px ${16 + level * 16}px`,
				cursor: "pointer",
				opacity: isDragging ? 0.5 : 1,
				borderRadius: "2px",
				display: "flex",
				alignItems: "center",
				fontFamily: "Roboto, Arial, sans-serif",
				fontSize: "14px",
				position: "relative",
				whiteSpace: "nowrap",
			}}
			onDoubleClick={() => onSelect(item.id)}
		>
			{isOver && dropPosition === "above" && <div className="drop-line" style={{ top: 0 }} />}
			{isOver && dropPosition === "below" && <div className="drop-line" style={{ bottom: 0 }} />}
			<div
				style={{ flex: 1, display: "flex", alignItems: "center" }}
				onClick={() => onSelect(item.id)}
			>
				<span
					onClick={toggleExpand}
					style={{
						marginRight: "4px",
						transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
						transition: "transform 0.2s",
						fontSize: "12px",
						color: "#5f6368",
						cursor: "pointer",
					}}
				>
					▶
				</span>
				<Dropdown
					menu={{
						items: [
							{
								key: "new-bookmark",
								label: "New bookmark",
								icon: <ExportOutlined />,
								onClick: () => onSelect(item.id, "bookmark"),
							},
							{
								key: "new-folder",
								label: "New folder",
								icon: <FolderOutlined />,
								onClick: () => onSelect(item.id, "folder"),
							},
							{
								key: "edit",
								label: "Rename",
								icon: <EditOutlined />,
								onClick: () => onSelect(item.id, "edit", item),
							},
							{
								key: "delete",
								label: "Delete",
								disabled: item.id === "1",
								icon: <DeleteOutlined />,
								onClick: () => onSelect(item.id, "delete"),
							},
						],
					}}
					trigger={["contextMenu"]}
				>
					<div style={{ display: "flex", alignItems: "center" }}>
						<FolderOutlined
							style={{
								marginRight: "8px",
								fontSize: "16px",
								color: "#5f6368",
							}}
						/>
						<span style={{ color: isSelected ? "#202124" : "#202124" }}>{item.title}</span>
					</div>
				</Dropdown>
			</div>
		</div>
	);
};

FolderTreeNode.displayName = "FolderTreeNode";

export default FolderTreeNode;
